/**
 * Der Prüfbericht – der einzige Ort, an dem die Freigaben stehen.
 *
 * Er hängt an seiner Prüfung und zeigt deren Stand: alle Kriterien der
 * Checkliste, ihr Urteil und, wo abgehakt wurde, die Bemerkung mit Person und
 * Zeitpunkt. Ältere Prüfungen bleiben unverändert – ihr Bericht zeigt den
 * damaligen Stand mit den damaligen Freigaben.
 *
 * Er aktualisiert sich mit: Wird ein Punkt im Prüfblock abgehakt, erscheint die
 * Bemerkung sofort hier, weil beide dieselbe Abfrage lesen.
 */

import { useState } from 'react';
import type { PruefLauf } from '../typen';
import './pruefbericht.css';

const STATUSWORT: Record<string, string> = {
  passt: 'passt',
  auffaellig: 'auffällig',
  nicht_ermittelbar: 'nicht ermittelbar',
};

export function Pruefbericht({ lauf }: { lauf: PruefLauf }) {
  const [offen, setOffen] = useState(false);

  const zahlen = {
    passt: lauf.ergebnisse.filter((e) => e.status === 'passt').length,
    auffaellig: lauf.ergebnisse.filter((e) => e.status === 'auffaellig').length,
    nichtErmittelbar: lauf.ergebnisse.filter((e) => e.status === 'nicht_ermittelbar').length,
    freigegeben: lauf.ergebnisse.filter((e) => !!e.freigabeAm).length,
  };
  const nochOffen = lauf.ergebnisse.filter((e) => e.status !== 'passt' && !e.freigabeAm).length;

  return (
    <div className={`br ${nochOffen === 0 ? 'br--gruen' : 'br--offen'}`}>
      <button type="button" className="br-kopf" onClick={() => setOffen((o) => !o)}>
        <span className="br-pdf">PDF</span>
        <span className="br-titel">Prüfbericht</span>
        <span className="br-pfeil">{offen ? '▴' : '▾'}</span>
      </button>

      {offen && (
        <div className="br-inhalt">
          <div className="br-quelle">
            Datei: {lauf.quelldateiName}
            <span className="br-trenner">·</span>
            {lauf.lieferantdateiName ? (
              <>
                Lieferantenangebot: {lauf.lieferantdateiName}
                <span className="br-trenner">·</span>
              </>
            ) : (
              <>
                <span>ohne Lieferantenangebot geprüft</span>
                <span className="br-trenner">·</span>
              </>
            )}
            {lauf.checkliste
              ? `Checkliste ${lauf.checkliste.kategorieLabel} · ${lauf.checkliste.dateiname}`
              : 'Ohne Checkliste – geprüft wurden Leistungsabgleich und Marge'}
          </div>

          <div className="br-liste">
            {lauf.ergebnisse.map((e) => {
              const frei = !!e.freigabeAm;
              return (
                <div key={e.id} className="br-zeile">
                  <div className="br-zeile-kopf">
                    <span className={`br-punkt br-punkt--${frei ? 'frei' : e.status}`}>
                      {frei || e.status === 'passt' ? '✓' : e.status === 'auffaellig' ? '!' : '?'}
                    </span>
                    <span className="br-name">{e.kriteriumText}</span>
                    <span className={`br-wort br-wort--${frei ? 'frei' : e.status}`}>
                      {frei ? `freigegeben · war ${STATUSWORT[e.status]}` : STATUSWORT[e.status]}
                    </span>
                  </div>

                  {frei && (
                    <div className="br-freigabe">
                      <p className="br-freigabe-text">„{e.freigabeBemerkung}"</p>
                      <div className="br-freigabe-fuss">
                        Als in Ordnung bewertet von {e.freigabeVon} ·{' '}
                        {e.freigabeAm ? new Date(e.freigabeAm).toLocaleString('de-AT') : ''}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="br-summe">
            {zahlen.passt} passt · {zahlen.auffaellig} auffällig · {zahlen.nichtErmittelbar} nicht
            ermittelbar · {zahlen.freigegeben} vom Prüfer freigegeben
            <span className="br-links">
              <a
                className="br-link"
                href={`/api/pruefung/${lauf.id}/pdf`}
                target="_blank"
                rel="noreferrer"
              >
                Geprüftes PDF öffnen
              </a>
              {lauf.hatLieferantSnapshot && (
                <a
                  className="br-link"
                  href={`/api/pruefung/${lauf.id}/pdf?rolle=lieferant`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Lieferantenangebot öffnen
                </a>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}